'use client';

import React, { useState, useEffect, useRef } from 'react';
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from 'recharts';
import { Brain, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useSession } from './SessionProvider';

function getCLICategory(value: number): {
  label: string;
  color: string;
  bgClass: string;
  borderClass: string;
  description: string;
} {
  if (value < 30) return { label: 'Low', color: '#6ee7b7', bgClass: 'bg-emerald-950/30', borderClass: 'border-emerald-800/40', description: 'Minimal cognitive demand — subject is relaxed or under-stimulated' };
  if (value < 55) return { label: 'Moderate', color: '#6ee7b7', bgClass: 'bg-emerald-950/20', borderClass: 'border-emerald-800/30', description: 'Optimal range — task-engaged without overload' };
  if (value < 75) return { label: 'Elevated', color: '#f59e0b', bgClass: 'bg-amber-950/30', borderClass: 'border-amber-800/40', description: 'High demand — monitor for fatigue onset' };
  return { label: 'Overload', color: '#ef4444', bgClass: 'bg-red-950/30', borderClass: 'border-red-800/40', description: 'Critical threshold exceeded — intervention recommended' };
}

// Animated counter hook
function useAnimatedValue(target: number, duration = 600) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const fromRef = useRef(target);

  useEffect(() => {
    const from = fromRef.current;
    const to = target;
    if (Math.abs(to - from) < 0.1) return;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startRef.current = null;

    const animate = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (to - from) * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return display;
}

export default function CLIRadialCard() {
  const { metrics, status } = useSession();
  const [prevCLI, setPrevCLI] = useState(metrics.cognitiveLoadIndex);
  const [displayCLI, setDisplayCLI] = useState(metrics.cognitiveLoadIndex);
  const [flash, setFlash] = useState(false);
  const [pulseIntensity, setPulseIntensity] = useState(0);
  const animatedCLI = useAnimatedValue(displayCLI);

  useEffect(() => {
    const prev = displayCLI;
    setPrevCLI(prev);
    setDisplayCLI(metrics.cognitiveLoadIndex);

    // Trigger flash on significant change
    if (Math.abs(metrics.cognitiveLoadIndex - prev) > 1) {
      setFlash(true);
      setTimeout(() => setFlash(false), 700);
    }

    // Pulse intensity based on CLI value (higher = more intense orange glow)
    setPulseIntensity(metrics.cognitiveLoadIndex / 100);
  }, [metrics.cognitiveLoadIndex]);

  const cat = getCLICategory(displayCLI);
  const diff = displayCLI - prevCLI;
  const chartData = [{ name: 'CLI', value: displayCLI, fill: cat.color }];

  // Orange glow intensity scales with cognitive load
  const glowOpacity = 0.15 + pulseIntensity * 0.45;
  const glowSize = 40 + pulseIntensity * 40;

  return (
    <div
      className={`rounded-xl border p-5 h-full min-h-[176px] flex flex-col gap-4 transition-all duration-500 relative overflow-hidden ${cat.bgClass} ${cat.borderClass}`}
      style={{
        boxShadow: flash
          ? `0 0 32px 8px rgba(249,115,22,0.55), 0 0 0 2px rgba(249,115,22,0.4)`
          : `0 0 ${glowSize}px rgba(249,115,22,${glowOpacity})`,
        transition: 'box-shadow 0.4s ease, background 0.5s ease',
      }}
    >
      {/* Pulsing orange glow overlay — animates continuously */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 50% 110%, rgba(249,115,22,${glowOpacity * 0.6}) 0%, transparent 65%)`,
          animation: 'cliPulse 2.8s ease-in-out infinite',
        }}
      />

      {/* Data-triggered flash highlight */}
      {flash && (
        <div
          className="absolute inset-0 pointer-events-none rounded-xl"
          style={{
            background: 'radial-gradient(ellipse at 50% 50%, rgba(249,115,22,0.18) 0%, transparent 70%)',
            animation: 'flashFade 0.7s ease-out forwards',
          }}
        />
      )}

      <div className="flex items-start justify-between relative z-10">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg bg-zinc-900/80 flex items-center justify-center"
            style={{
              boxShadow: `0 0 10px rgba(249,115,22,${0.2 + pulseIntensity * 0.4})`,
              animation: 'iconPulse 2.8s ease-in-out infinite',
            }}
          >
            <Brain size={16} style={{ color: cat.color }} />
          </div>
          <div>
            <p className="text-[12px] font-medium text-zinc-400 tracking-wide">Cognitive Load Index</p>
            <p className="text-[11px] text-zinc-600">Composite EEG workload score</p>
          </div>
        </div>
        <div
          className="px-2.5 py-1 rounded-full text-[11px] font-semibold border"
          style={{ color: cat.color, borderColor: cat.color + '40', backgroundColor: cat.color + '15' }}
        >
          {cat.label}
        </div>
      </div>

      <div className="flex items-center gap-4 flex-1 relative z-10">
        {/* Radial chart */}
        <div className="w-28 h-28 flex-shrink-0 relative">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="68%"
              outerRadius="100%"
              startAngle={220}
              endAngle={-40}
              barSize={10}
              data={chartData}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar
                dataKey="value"
                cornerRadius={6}
                background={{ fill: 'rgba(255,255,255,0.04)' }}
              />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span
              className="text-[22px] font-bold font-mono tabular-nums text-zinc-100"
              style={{ transition: 'color 0.3s ease' }}
            >
              {Math.round(animatedCLI)}
            </span>
            <span className="text-[10px] text-zinc-500">/ 100</span>
          </div>
        </div>

        {/* Metadata */}
        <div className="flex-1 min-w-0">
          <p className="text-[12px] text-zinc-500 leading-relaxed mb-3">{cat.description}</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            {[
              { label: 'Delta', value: metrics.deltaPower.toFixed(1), unit: 'µV²', color: 'text-blue-400' },
              { label: 'Theta', value: metrics.thetaPower.toFixed(1), unit: 'µV²', color: 'text-violet-400' },
              { label: 'Alpha', value: metrics.alphaPower.toFixed(1), unit: 'µV²', color: 'text-emerald-400' },
              { label: 'Gamma', value: metrics.gammaPower.toFixed(1), unit: 'µV²', color: 'text-pink-400' },
            ].map((band) => (
              <div key={`cli-band-${band.label}`} className="flex items-center gap-1.5">
                <span className={`text-[11px] font-medium ${band.color}`}>{band.label}</span>
                <span className="text-[11px] font-mono tabular-nums text-zinc-400">{band.value}</span>
                <span className="text-[10px] text-zinc-600">{band.unit}</span>
              </div>
            ))}
          </div>
          {/* Trend */}
          <div className="flex items-center gap-1.5 mt-3">
            {Math.abs(diff) < 0.5 ? (
              <Minus size={12} className="text-zinc-500" />
            ) : diff > 0 ? (
              <TrendingUp size={12} className="text-red-400" />
            ) : (
              <TrendingDown size={12} className="text-emerald-400" />
            )}
            <span className="text-[11px] text-zinc-500">
              {Math.abs(diff) < 0.5
                ? 'Stable'
                : `${diff > 0 ? '+' : ''}${diff.toFixed(1)} from last sample`}
            </span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes cliPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.04); }
        }
        @keyframes iconPulse {
          0%, 100% { box-shadow: 0 0 8px rgba(249,115,22,0.25); }
          50% { box-shadow: 0 0 18px rgba(249,115,22,0.6); }
        }
        @keyframes flashFade {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}